import React, { useEffect, useState, useRef } from "react";
import Countdown from "./components/Countdown";
import DetailWidget from "./components/DetailWidget";
import FilterWidget from "./components/FilterWidget";
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import StreamModal from "./components/StreamModal";
import LoadingScreen from "./components/LoadingScreen";
import rocketIcon from "./images/icon_red.webp";
import "./starfield.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";
const CACHE_KEY = "blastoff_launch_cache";
const CACHE_DURATION_MS = 5 * 60 * 1000;
const STREAM_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours

function App() {
  const [launchList, setLaunchList] = useState([]);
  const [launchTime, setLaunchTime] = useState(null);
  const [launchName, setLaunchName] = useState("");
  const [selectedLaunch, setSelectedLaunch] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [filters, setFilters] = useState({
    spacexOnly: false,
    blueOriginOnly: false,
    rocketLabsOnly: false,
    unitedLaunchAllianceOnly: false,
    arianespaceOnly: false,
    nasaOnly: false,
    colourStatus: false,
  });

  const [streamOpened, setStreamOpened] = useState(false);
  const [streamUrl, setStreamUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdownFontSize, setCountdownFontSize] = useState(4);

  const handleCountdownSizeChange = (delta) => {
    setCountdownFontSize((prev) => Math.min(6, Math.max(2, parseFloat((prev + delta).toFixed(1)))));
  };
  const modalTimerRef = useRef(null);
  const streamAnchorTimeRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const retryWindowMs = 2 * 60 * 1000; // 2 minutes
    const baseDelayMs = 2000; // 2 seconds between tries
    const startTime = Date.now();

    function getCachedLaunches() {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;
        const parsed = JSON.parse(cached);
        if (
          parsed.timestamp &&
          Date.now() - parsed.timestamp < CACHE_DURATION_MS &&
          parsed.launchList
        ) {
          return parsed.launchList;
        }
        return null;
      } catch {
        return null;
      }
    }

    async function fetchLaunches() {
      try {        
        setLoading(true);
        const url = new URL("/launches", BASE_URL);
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        const list = data.results || [];

        if (isMounted) {
          setLaunchList(list);
          setLoading(false); // ✅ Only set false when fetch succeeds
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ timestamp: Date.now(), launchList: list })
          );
        }
      } catch (error) {
        const elapsed = Date.now() - startTime;
        if (elapsed < retryWindowMs) {
          // Retry after delay without clearing loading
          setTimeout(fetchLaunches, baseDelayMs);
        } else if (isMounted) {
          // After full retry window, stop loading and show error
          setErrorMsg("Unable to connect to the server. Please try again later.");
          setLoading(false);
        }
      }
    }

    const cached = getCachedLaunches();
    if (cached) {
      setLaunchList(cached);
      setLoading(false);
    } else {
      fetchLaunches();
    }

    return () => {
      isMounted = false;
    };
  }, []);

 useEffect(() => {
  const now = new Date();

  const isMatchingProvider = (provider) => {
    const p = provider.toLowerCase();
    const active = [
      filters.spacexOnly && "spacex",
      filters.blueOriginOnly && "blue origin",
      filters.rocketLabsOnly && "rocket lab",
      filters.unitedLaunchAllianceOnly && "united launch alliance",
      filters.arianespaceOnly && "arianespace",
      filters.nasaOnly && "national aeronautics and space administration",
    ].filter(Boolean);

    if (active.length === 0) return true;
    return active.some((term) => p.includes(term));
  };

  const nextLaunch = filters.colourStatus
    // Colour Status ON: show any upcoming launch, colour communicates status
    ? launchList.find((launch) => {
        const isUpcoming = new Date(launch.net) > now;
        const provider = launch.launch_service_provider?.name || "";
        return isUpcoming && isMatchingProvider(provider);
      }) ?? null
    // Colour Status OFF: Go for Launch only, white circle
    : launchList.find((launch) => {
        const isUpcoming = new Date(launch.net) > now;
        const isGo = launch.status?.name?.toLowerCase() === "go for launch";
        const provider = launch.launch_service_provider?.name || "";
        return isUpcoming && isGo && isMatchingProvider(provider);
      }) ?? null;

  if (nextLaunch) {
    setSelectedLaunch((prev) => {
      if (prev?.id !== nextLaunch.id) {
        const withinWindow = streamAnchorTimeRef.current && Date.now() < streamAnchorTimeRef.current + STREAM_WINDOW_MS;
        if (!withinWindow) {
          setStreamOpened(false);
          setStreamUrl(null);
        }
      }
      return nextLaunch;
    });

    setLaunchTime(nextLaunch.net);
    setLaunchName(nextLaunch.name);
    setErrorMsg("");
  } else if (!loading) {
    setLaunchTime(null);
    setLaunchName("No matching upcoming launches found");
    setSelectedLaunch(null);
  }
}, [launchList, filters, loading]);

  const handleCloseModal = () => {
    setStreamOpened(false);
    setStreamUrl(null);
    clearTimeout(modalTimerRef.current);
    streamAnchorTimeRef.current = null;

    const now = new Date();
    const nextLaunch = launchList.find((launch) => {
      const isUpcoming = new Date(launch.net) > now;
      const isGo = launch.status?.name?.toLowerCase() === "go for launch";
      return isUpcoming && isGo;
    });

    if (nextLaunch) {
      setLaunchTime(nextLaunch.net);
      setLaunchName(nextLaunch.name);
      setSelectedLaunch(nextLaunch);
    } else {
      setLaunchTime(null);
      setLaunchName("No upcoming launches found");
      setSelectedLaunch(null);
    }
  };

  // Show loading screen until finished or failed
  if (loading) return <LoadingScreen errorMsg={errorMsg} />;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-white p-4 relative overflow-x-hidden">
      <div className="stars"></div>
      <div className="gascloud"></div>
      <div className="cloud"></div>
      <Analytics />
      <SpeedInsights />
      <FilterWidget filters={filters} setFilters={setFilters} countdownFontSize={countdownFontSize} onCountdownSizeChange={handleCountdownSizeChange} />
      <DetailWidget launch={selectedLaunch} />

      <div className="flex flex-col md:flex-row items-center justify-between z-10 w-full max-w-6xl">
        <div className="flex flex-col items-center flex-grow text-center mb-8 md:mb-0">
          <div className={`circle ${filters.colourStatus ? (selectedLaunch?.status?.name?.toLowerCase() === "go for launch" ? "circle-go" : selectedLaunch ? "circle-pending" : "") : ""}`}>
            <img
              src={rocketIcon}
              alt="Blast Off"
              className="w-[15vmin] h-auto mb-[2vmin]"
            />

            {errorMsg ? (
              <p className="text-red-400 text-[1.8vmin]">{errorMsg}</p>
            ) : launchTime ? (
              <>
                <h2 className="text-[3vmin] mb-[1vmin] font-orbitron">
                  {launchName}
                </h2>
                <div className="font-orbitron p-[5vmin]" style={{ fontSize: `${countdownFontSize}vmin` }}>
                  <Countdown
                    targetDate={launchTime}
                    onTenSecondMark={async () => {
                      if (!streamOpened && selectedLaunch) {
                        let stream = selectedLaunch?.vid_urls?.[0]?.url || null;

                        if (!stream) {
                          try {
                            const provider = selectedLaunch.launch_service_provider?.name || "";
                            const name = selectedLaunch?.name || "";
            const res = await fetch(`${BASE_URL}/stream?provider=${encodeURIComponent(provider)}&name=${encodeURIComponent(name)}`);
                            if (res.ok) {
                              const data = await res.json();
                              stream = data.url || null;
                            }
                          } catch (err) {
                            console.error("YouTube stream lookup failed:", err);
                          }
                        }

                        if (stream) {
                          streamAnchorTimeRef.current = Date.now();
                          setStreamUrl(stream);
                          setStreamOpened(true);
                          modalTimerRef.current = setTimeout(() => {
                            handleCloseModal();
                          }, STREAM_WINDOW_MS);
                        }
                      }
                    }}
                  />
                </div>
              </>
            ) : (
              <p className="text-[2vmin]">
                {launchName || "Loading launch data..."}
              </p>
            )}
          </div>
        </div>
      </div>

      <StreamModal
        url={streamOpened ? streamUrl : null}
        onClose={handleCloseModal}
      />
    </div>
  );
}

export default App;
